import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Graph, GraphData, GraphNode, GraphLink } from "react-d3-graph";
import { conf, Schema, Resource, Attribute, Field, RelSide, RelPoint, Relationship, RelType, RelLink } from 'src/config';
import _, { Dictionary } from 'lodash';
import Entity from '../../components/Entity';
import * as selectors from '../../selectors';
import * as types from '../../types';
import * as actions from '../../actions';
import { getPorts, toRelLink, isEqualLink, isTemp, isRoot, isEqualLocator, locateEntity, isTransient } from '../../utils';
import { getColor } from './utils';

interface Node extends GraphNode {
    label: string;
    fontSize?: number;
    fx?: number;
    fy?: number;
}

interface Link extends GraphLink {
    strokeDasharray?: string;
}

const colorMap: Dictionary<string> = {};

const getResourceColor: (resourceName: string) => string = (resouceName) => {
    let color: string | undefined = colorMap[resouceName];
    if (color == null) {
        color = getColor(_.entries(colorMap).length);
        colorMap[resouceName] = color;
    }
    return color;
}

const getNodeId: (resource: string, id: string) => string = (resource, id) => `${resource}(${id})`;


const GraphView: React.FC<{
    graphId: string,
    height: number,
    width: number,
}> = ({
    graphId,
    height,
    width,
}) => {
        const dispatch = useDispatch();
        const state: types.Explorer = useSelector(selectors.SelectState());
        const entities: types.Entities = state.entities;
        const stack: types.StackItem[] = state.stack;
        const [data, setData] = useState<GraphData<Node, Link>>({ nodes: [], links: [] });

        const getNode: (resource: string, id: string, node: types.Entity) => Node = (resource, id, node) => {
            const nodeId = getNodeId(resource, id);
            const nodeLocator = { name: resource, id };

            return {
                id: nodeId,
                label: isTemp(id) ? `${resource}*` : nodeId,
                color: getResourceColor(resource),
                size: (node.full) ? 500 : 50,
                opacity: (!node.full) ? 0.2 : (isTemp(id) || isTransient(id)) ? 0.3 : undefined,
                strokeColor: isEqualLocator(_.last(stack)!.locator, nodeLocator) ? 'black' : undefined,
                fontSize: (!node.full) ? 4 : undefined,
            }
        }

        const getLink: (source: types.Locator, target: types.Locator) => Link = (source, target) => {
            const sourceNode = locateEntity(entities, source);
            const targetNode = locateEntity(entities, target);
            const lazy = !(sourceNode.full && targetNode.full);

            return {
                source: getNodeId(source.name, source.id),
                target: getNodeId(target.name, target.id),
                strokeDasharray: lazy ? '6 3' : undefined,
                opacity: lazy ? 0.2 : undefined,
            }
        }

        useEffect(
            () => {
                const data: GraphData<Node, Link> = {
                    nodes: [],
                    links: []
                }

                const relLinks: RelLink[] = [];

                _.entries(entities)
                    .forEach(entry => {
                        const [resource, nodes] = entry;

                        _.entries(nodes)
                            .filter(entry => entry[1].full)
                            .forEach(entry => {
                                const [id, node] = entry;
                                data.nodes.push(getNode(resource, id, node));
                                const ports: types.RelPort[] = getPorts(node, resource);

                                ports.filter(port => {
                                    const { locator, relPoint: { rel, side } } = port;
                                    const pointee: types.Entity = locateEntity(entities, locator);
                                    return pointee.full;
                                }).forEach(port => {
                                    const relLink: RelLink = toRelLink(port, { name: resource, id });
                                    relLinks.push(relLink);
                                });
                            });
                    });

                const uniqLinks: RelLink[] = _.uniqWith(relLinks, (a, b) => isEqualLink(a, b))
                uniqLinks.forEach(link => {
                    const { resource: source, related: target } = link;
                    data.links.push(getLink(source, target));
                });

                if (data.nodes.length == 1) {
                    data.nodes[0].fx = height / 2;
                    data.nodes[0].fy = width / 2;
                }

                setData(data);
            },
            [entities, stack]
        );

        const graphConf = {
            nodeHighlightBehavior: true,
            automaticRearrangeAfterDropNode: true,
            focusAnimationDuration: 0.75,
            focusZoom: 1,
            // focusedNodeId: 'app:118',
            node: {
                // color: "lightgreen",
                // size: 500,
                // fontSize: 10,
                highlightStrokeColor: "blue",

                labelProperty: (node: Node) => {
                    return node.label;
                }
            },
            link: {
                highlightColor: "lightblue",
            },
            height: height,
            width: width,
            initialZoom: 1.5,
            d3: {
                // alphaTarget: 0.05,
                // gravity: -100,
                // linkLength: 100,
                // linkStrength: 1,
                // disableLinkForce: false

                gravity: -50,
            }
        };

        // `Clicked node ${nodeId}`
        const onClickNode = (nodeId: any) => {
        };

        // `Clicked link between ${source} and ${target}`
        const onClickLink = (source: any, target: any) => {
        };

        // `Double clicked node ${nodeId}`
        const onDoubleClickNode = (nodeId: any) => {
        };

        return data.nodes.length > 0 ? <Graph
            id={graphId}
            data={data}
            config={graphConf}
            onClickNode={onClickNode}
            onDoubleClickNode={onDoubleClickNode}
            onClickLink={onClickLink}

        /> : null;
    };

export default GraphView;
